import { useLoaderData, useOutletContext } from "@remix-run/react";
import type { ShouldRevalidateFunction } from "@remix-run/react";
import { useMemo } from "react";
import { loader } from "~/routes-server/reviews.server";
import { metaHelper } from "~/lib/meta";
import type { headers } from "@commercekit/remix/lib.server";
import { ReviewsSection, ReviewsInfoSection, CustomerReviewsSection } from "~/components/reviews";
import type { SliceComponentType } from "@prismicio/react";
import { SliceZoneC } from "@commercekit/prismic";
import { components } from "~/lib/prismic";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import type { OutletContext } from "~/components/layout/Layout";
import type { MetaFunction } from "@remix-run/node";

export { default as ErrorBoundary } from "~/components/atoms/GeneralErrorBoundary";
export { loader };
export { headers };

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const base = metaHelper({ data }) ?? [];
  const schema = (data as any)?.reviewsSchema != null
    ? [{ "script:ld+json": (data as any).reviewsSchema } as const]
    : [];
  return [...base, ...schema];
};

// Prevent unnecessary revalidation from /revalidate action
export const shouldRevalidate: ShouldRevalidateFunction = ({ formAction, defaultShouldRevalidate }) => {
  // Don't revalidate when /revalidate action is called
  if (formAction === "/revalidate") {
    return false;
  }
  // Use default Remix behavior for other cases
  return defaultShouldRevalidate;
};

export default function ReviewsPage() {
  const data = useLoaderData<typeof loader>();
  const { $pt } = usePrismicLabels();
  const { headerRating } = useOutletContext<OutletContext>();

  // Stabilize the data with useMemo to prevent unnecessary re-renders  
  const pageData = useMemo(() => {
    const typedData = data as any;
    return typedData;
  }, [data]);

  // If this is a Prismic page (for non-NL/DE/fr/be locales), render it
  if (pageData.pageType === "prismic") {
    
    if (pageData.prismicPageNotFound) {
      return (
        <div className="container py-10">
          <h1 className="heading strong xxlg reviews_page_title"> {$pt("main_reviews_page_title")}</h1>
          <p className="mt-4">{$pt("main_reviews_page_error_message")}</p>
        </div>
      );
    }

    // Ensure we have Prismic data before rendering
    if (!pageData.customContentPage) {
      return (
        <div className="container py-10">
          <h1 className="heading strong xxlg reviews_page_title"> {$pt("main_reviews_page_title")}</h1>
          <p className="mt-4">{$pt("main_reviews_page_error_message")}</p>
        </div>
      );
    }

    return (
      <div>
        <SliceZoneC
          slices={pageData.customContentPage?.data?.slices || []}
          components={components as Record<string, SliceComponentType>}
          defaultComponent={({ slice }) => (
            <div className="hidden">{slice.slice_type}</div>
          )}
        />
      </div>
    );
  }

  return (
    <>
      {/* Hide footer review sections on this page */}
      <style>{`
        .footer__quote,
        .footer__reviews {
          display: none !important;
        }
      `}</style>

      <div className="container">
        <h1 className="heading xxlg py-3 reviews_page_title"> {$pt("main_reviews_page_title")}</h1>
        <ReviewsInfoSection />
      </div>
      {pageData.channels.length > 0 && (
        <ReviewsSection
          channel={pageData.channel}
          channels={pageData.channels || []}
          reviews={pageData.reviews}
          scores={pageData.scores}
          allChannelScores={pageData.allChannelScores}
          brandImage={pageData.brandImage}
          logo={pageData.logo}
        />
      )}

      {/* Customer Reviews Section */}
      {pageData.reviews?.items && pageData.reviews.items.length > 0 && (
        <CustomerReviewsSection
          reviews={pageData.reviews.items}
          maxReviews={6}
        />
      )}
    </>
  );
}
